import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  PreloadedQuery,
  useMutation,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay';
import { graphql } from 'relay-runtime';
import type {
  GlobalContextProviderQuery as GlobalContextProviderQueryType,
  GlobalContextProviderQuery$data as GlobalContextProviderQueryResponse,
  SettingLanguage,
} from './__generated__/GlobalContextProviderQuery.graphql';
import { useLoaderData } from 'react-router-dom';
import { LoaderData, PageAccess } from './Types';
import { GlobalContextProviderUpdateEntityThemeMutation$data } from './__generated__/GlobalContextProviderUpdateEntityThemeMutation.graphql';
import { GlobalContextProviderSettingUpdateLanguageMutation$data } from './__generated__/GlobalContextProviderSettingUpdateLanguageMutation.graphql';
import { ThemeProvider } from '@mui/material';
import { darkTheme } from '../portals/DarkTheme';
import { lightTheme } from '../portals/LightTheme';
import {
  getNetworkErrorMessage,
  getSystemTheme,
  useSendErrorReport,
} from './Tools';
import classNames from 'classnames';
import layoutStyles from '../../../stylesheets/components/layout.module.scss';
import pluralize from 'pluralize';
import { usePortalAlert } from '../tenant_portal/PortalAlertContext';
import { useOptionalPortalMutation } from '../tenant_portal/interaction_lock/usePortalMutation';

export const GlobalContextProviderQuery = graphql`
  query GlobalContextProviderQuery {
    entities(returnSelf: true) {
      edges {
        node {
          __id
          __typename
          login {
            email
          }
          theme
        }
      }
    }
    license {
      id
      timeZone
      freeTrialLicense
      hasUnitPostingsFeature
      hasLeadOrganizationFeature
      hasLeadAutomationFeature
      chargeAchFeeTo
      termOverrides {
        term
        override
      }
      marketingAccess {
        noAccess
        restrictedAccess
      }
      checkScanningAccess {
        noAccess
        restrictedAccess
      }
    }
    setting {
      language
    }
  }
`;

export const GlobalContextProviderUpdateEntityThemeMutation = graphql`
  mutation GlobalContextProviderUpdateEntityThemeMutation(
    $input: EntityUpdateThemeInput!
  ) {
    entityUpdateTheme(input: $input) {
      errors
      entity {
        __typename
        theme
      }
    }
  }
`;

export const GlobalContextProviderSettingUpdateLanguageMutation = graphql`
  mutation GlobalContextProviderSettingUpdateLanguageMutation(
    $input: SettingUpdateLanguageInput!
  ) {
    settingUpdateLanguage(input: $input) {
      setting {
        language
      }
      errors {
        path
        messages
      }
    }
  }
`;

type UserLanguage = 'en' | 'es' | 'fr';

const normalizeBrowserLanguage = (
  browserLanguage: string | undefined
): UserLanguage => {
  const primarySubtag = browserLanguage?.split('-')[0]?.toLowerCase();
  if (
    primarySubtag === 'en' ||
    primarySubtag === 'es' ||
    primarySubtag === 'fr'
  ) {
    return primarySubtag;
  }
  return 'en';
};

interface GlobalContext {
  theme: string | null;
  licenseId?: string | undefined;
  licenseChargeAchFeeTo: string | null;
  loginEmail?: string | undefined;
  timeZone?: string | undefined;
  toggleTheme?: () => void;
  terms?: string[];
  marketingAccess?: PageAccess;
  checkScanningAccess?: PageAccess;
  freeTrialLicense?: boolean;
  hasUnitPostingsFeature?: boolean;
  hasLeadOrganizationFeature?: boolean;
  hasLeadAutomationFeature?: boolean;
  currentLanguage?: SettingLanguage;
  savedLanguage?: SettingLanguage | null;
  getTermOverride: (
    term: string,
    capitalize?: boolean,
    pluralize?: boolean
  ) => string;
}

export const GlobalContext = createContext<GlobalContext>({
  theme: null,
  licenseId: undefined,
  licenseChargeAchFeeTo: null,
  loginEmail: undefined,
  timeZone: undefined,
  toggleTheme: () => undefined,
  terms: [],
  marketingAccess: undefined,
  checkScanningAccess: undefined,
  freeTrialLicense: undefined,
  hasUnitPostingsFeature: undefined,
  hasLeadOrganizationFeature: undefined,
  hasLeadAutomationFeature: undefined,
  currentLanguage: undefined,
  savedLanguage: undefined,
  getTermOverride: () => '',
});

interface GlobalContextProviderProps {
  children: React.ReactNode;
  isLoggedIn: boolean;
  preloadedQuery?: PreloadedQuery<GlobalContextProviderQueryType>;
  syncHtmlThemeDark1?: boolean;
}

// Login/portal dark mode uses `.dark` on a React wrapper; management site uses
// `theme-dark1` on `<html>`. CSS variables in globals.module.scss only flip under
// `:root.theme-dark1`, so sync that class when the portal is in dark mode.
const isPortalDarkTheme = (theme: string | null) =>
  theme === 'dark' || theme === 'dark1';
const MANAGEMENT_SITE_THEME_CHANGE_EVENT = 'propertyMatrix:themeChange';

const useHtmlThemeDark1Sync = (theme: string | null, enabled = false) => {
  useEffect(() => {
    if (!enabled) return;

    const isDark = isPortalDarkTheme(theme);
    document.documentElement.classList.toggle('theme-dark1', isDark);

    return () => {
      document.documentElement.classList.remove('theme-dark1');
    };
  }, [enabled, theme]);
};

const LoggedOutGlobalContextProvider = ({
  children,
  syncHtmlThemeDark1 = false,
}: {
  children: React.ReactNode;
  syncHtmlThemeDark1?: boolean;
}) => {
  const [theme, setTheme] = useState(getSystemTheme);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setTheme(getSystemTheme());
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useHtmlThemeDark1Sync(theme, syncHtmlThemeDark1);

  const getTermOverride = (
    term: string,
    capitalizeTerm?: boolean,
    pluralizeTerm?: boolean
  ) => {
    let override = term;
    if (capitalizeTerm) {
      override = override
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    if (pluralizeTerm) {
      override = pluralize(override);
    }
    return override;
  };

  const isDark = isPortalDarkTheme(theme);

  return (
    <div
      className={classNames(layoutStyles.fullHeight, layoutStyles.fullWidth, {
        dark: isDark,
      })}
    >
      <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
        <GlobalContext.Provider
          value={{
            theme,
            licenseId: undefined,
            licenseChargeAchFeeTo: null,
            loginEmail: undefined,
            timeZone: undefined,
            getTermOverride,
          }}
        >
          {children}
        </GlobalContext.Provider>
      </ThemeProvider>
    </div>
  );
};

export const GlobalContextProvider = ({
  children,
  isLoggedIn,
  preloadedQuery,
  syncHtmlThemeDark1,
}: GlobalContextProviderProps) => {
  if (isLoggedIn)
    if (preloadedQuery) {
      return (
        <RelayThemeProviderLoadQueryWrapper preloadedQuery={preloadedQuery}>
          {children}
        </RelayThemeProviderLoadQueryWrapper>
      );
    } else {
      return (
        <RelayThemeProviderRouterWrapper>
          {children}
        </RelayThemeProviderRouterWrapper>
      );
    }
  else {
    return (
      <LoggedOutGlobalContextProvider syncHtmlThemeDark1={syncHtmlThemeDark1}>
        {children}
      </LoggedOutGlobalContextProvider>
    );
  }
};

const RelayThemeProviderRouterWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const loaderQueryReference = (useLoaderData() as LoaderData)
    .GlobalContextProviderLoadQuery;
  const [queryReference] = useQueryLoader<GlobalContextProviderQueryType>(
    GlobalContextProviderQuery,
    loaderQueryReference
  );

  const data = usePreloadedQuery<GlobalContextProviderQueryType>(
    GlobalContextProviderQuery,
    queryReference as PreloadedQuery<
      GlobalContextProviderQueryType,
      GlobalContextProviderQueryResponse
    >
  );

  return <RelayThemeProvider data={data}>{children}</RelayThemeProvider>;
};

const RelayThemeProviderLoadQueryWrapper = ({
  children,
  preloadedQuery,
}: {
  children: React.ReactNode;
  preloadedQuery: PreloadedQuery<GlobalContextProviderQueryType>;
}) => {
  useEffect(() => {
    return () => preloadedQuery.dispose();
  }, [preloadedQuery]);

  const data = usePreloadedQuery<GlobalContextProviderQueryType>(
    GlobalContextProviderQuery,
    preloadedQuery as PreloadedQuery<
      GlobalContextProviderQueryType,
      GlobalContextProviderQueryResponse
    >
  );

  return <RelayThemeProvider data={data}>{children}</RelayThemeProvider>;
};

const RelayThemeProvider = ({
  children,
  data,
}: {
  children: React.ReactNode;
  data: GlobalContextProviderQueryResponse;
}) => {
  const showAlert = usePortalAlert();
  const currentEntity = data?.entities?.edges?.[0]?.node ?? null;
  const [theme, setTheme] = useState(currentEntity?.theme || null);
  const licenseId = data?.license?.id;
  const loginEmail = currentEntity?.login?.email;
  const licenseChargeAchFeeTo = data.license.chargeAchFeeTo ?? null;
  const timeZone = data?.license?.timeZone;
  const marketingAccess = data?.license?.marketingAccess;
  const checkScanningAccess = data?.license?.checkScanningAccess;
  const freeTrialLicense = data?.license?.freeTrialLicense;
  const hasUnitPostingsFeature = data?.license?.hasUnitPostingsFeature;
  const hasLeadOrganizationFeature = data?.license?.hasLeadOrganizationFeature;
  const hasLeadAutomationFeature = data?.license?.hasLeadAutomationFeature;

  const savedLanguage = data.setting.language ?? null;
  const currentLanguage =
    savedLanguage ?? normalizeBrowserLanguage(navigator.language);

  const entityId = currentEntity?.__id || null;
  const entityTypename = currentEntity?.__typename || null;

  const [commitMutation] = useOptionalPortalMutation(
    GlobalContextProviderUpdateEntityThemeMutation,
    'Updating display settings'
  );
  const [commitLanguageUpdate] = useMutation(
    GlobalContextProviderSettingUpdateLanguageMutation
  );
  const sendErrorReport = useSendErrorReport();
  const sendErrorReportRef = useRef(sendErrorReport);
  const isExternalThemeSync = useRef(false);

  useEffect(() => {
    sendErrorReportRef.current = sendErrorReport;
  }, [sendErrorReport]);

  useEffect(() => {
    if (entityTypename !== 'Tenant' || savedLanguage != null) return;

    commitLanguageUpdate({
      variables: {
        input: {
          language: currentLanguage,
          initializeIfUnset: true,
        },
      },
      onCompleted: (response) => {
        const payload = (
          response as GlobalContextProviderSettingUpdateLanguageMutation$data
        ).settingUpdateLanguage;
        if (!payload) {
          sendErrorReportRef.current(
            'Malformed Language Update Response in GlobalContextProvider',
            `Malformed response data in GlobalContextProviderSettingUpdateLanguageMutation: ${JSON.stringify(response)}`
          );
          return;
        }
        if (payload.errors.length > 0) {
          sendErrorReportRef.current(
            'Error Initializing Language in GlobalContextProvider',
            payload.errors.map((error) => error.messages.join(', ')).join('; ')
          );
        }
      },
      onError: (error) => {
        sendErrorReportRef.current(
          'Error Initializing Language in GlobalContextProvider',
          error.message
        );
      },
    });
  }, [commitLanguageUpdate, currentLanguage, entityTypename, savedLanguage]);

  const updateTheme = useCallback(
    (theme: string) => {
      if (!entityId || !entityTypename) return;

      commitMutation({
        variables: {
          input: {
            entityThemeData: {
              id: entityId,
              theme: theme,
            },
          },
        },
        onCompleted: (response) => {
          const responseData =
            response as GlobalContextProviderUpdateEntityThemeMutation$data;
          if (!responseData || !responseData.entityUpdateTheme) {
            sendErrorReportRef.current(
              'Malformed Theme Update Response in GlobalContextProvider',
              `Malformed response data in GlobalContextProviderUpdateEntityThemeMutation: ${JSON.stringify(response)}`
            );
            showAlert('There was an error updating the theme');
            return;
          }
          if (
            responseData.entityUpdateTheme.errors &&
            responseData.entityUpdateTheme.errors.length > 0
          ) {
            showAlert(responseData.entityUpdateTheme.errors.join('<br>'));
            return;
          }
        },
        onError: (error) => {
          sendErrorReportRef.current(
            'Error Updating Theme in GlobalContextProvider',
            error.message
          );
          showAlert(
            getNetworkErrorMessage(
              'There was an error updating the theme',
              error
            )
          );
          console.error(error);
        },
        optimisticResponse: {
          entityUpdateTheme: {
            entity: {
              id: entityId,
              __typename: entityTypename,
              theme,
            },
            errors: null,
          },
        },
      });
    },
    [commitMutation, entityId, entityTypename, showAlert]
  );

  useEffect(() => {
    if (isExternalThemeSync.current) {
      isExternalThemeSync.current = false;
      return;
    }

    if (theme && entityId && entityTypename && theme !== currentEntity?.theme) {
      updateTheme(theme);
    }
  }, [currentEntity?.theme, entityId, entityTypename, theme, updateTheme]);

  useEffect(() => {
    const handleManagementSiteThemeChange = () => {
      const nextTheme = document.documentElement.classList.contains(
        'theme-dark1'
      )
        ? 'dark1'
        : 'standard';

      setTheme((currentTheme) => {
        if (currentTheme === nextTheme) return currentTheme;

        isExternalThemeSync.current = true;
        return nextTheme;
      });
    };

    window.addEventListener(
      MANAGEMENT_SITE_THEME_CHANGE_EVENT,
      handleManagementSiteThemeChange
    );

    return () => {
      window.removeEventListener(
        MANAGEMENT_SITE_THEME_CHANGE_EVENT,
        handleManagementSiteThemeChange
      );
    };
  }, []);

  const toggleTheme = () => {
    setTheme(theme === 'standard' ? 'dark' : 'standard');
  };

  if (!theme) setTheme(getSystemTheme());

  const terms = data?.license?.termOverrides.map(
    (termOverride) => termOverride.term
  );
  const termOverrideHash = (data?.license?.termOverrides || []).reduce(
    (acc: Record<string, string>, termOverride) => {
      acc[termOverride.term] = termOverride.override;
      return acc;
    },
    {}
  );
  const getTermOverride = useCallback(
    (term: string, capitalizeTerm?: boolean, pluralizeTerm?: boolean) => {
      let override = termOverrideHash[term] || '';
      if (capitalizeTerm) {
        override = override
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      if (pluralizeTerm) {
        override = pluralize(override);
      }
      return override;
    },
    [termOverrideHash]
  );

  const isDark = isPortalDarkTheme(theme);

  return (
    <div
      className={classNames(layoutStyles.fullHeight, layoutStyles.fullWidth, {
        dark: isDark,
      })}
    >
      <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
        <GlobalContext.Provider
          value={{
            theme,
            licenseId,
            licenseChargeAchFeeTo,
            loginEmail,
            timeZone,
            toggleTheme,
            terms,
            marketingAccess,
            checkScanningAccess,
            freeTrialLicense,
            hasUnitPostingsFeature,
            hasLeadOrganizationFeature,
            hasLeadAutomationFeature,
            currentLanguage,
            savedLanguage,
            getTermOverride,
          }}
        >
          {children}
        </GlobalContext.Provider>
      </ThemeProvider>
    </div>
  );
};

export const useGlobalContext = () => useContext(GlobalContext);
